import type {
  CanvasChatLink,
  Share,
  Snapshot,
  APIResponse,
} from "./types";

export interface CanvasAPIOptions {
  baseUrl: string;
  getAuthHeaders: () => Record<string, string>;
}

export class CanvasAPI {
  private baseUrl: string;
  private getAuthHeaders: () => Record<string, string>;

  constructor(opts: CanvasAPIOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, "");
    this.getAuthHeaders = opts.getAuthHeaders;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...this.getAuthHeaders(),
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    const json = (await res.json()) as APIResponse<T>;

    if (!json.success) {
      const msg =
        typeof json.error === "object" && json.error?.message
          ? json.error.message
          : typeof json.error === "string"
            ? json.error
            : `Request failed: ${res.status}`;
      throw new Error(msg);
    }

    return json.data as T;
  }

  // Session resolution
  async getCanvasForSession(sessionId: string): Promise<CanvasChatLink | null> {
    return this.request<CanvasChatLink | null>("GET", `/api/v1/canvas-session/${sessionId}`);
  }

  // Sharing
  async createShare(
    canvasId: string,
    input: { mode: string; password?: string; allowInteraction?: boolean },
  ): Promise<Share> {
    return this.request<Share>("POST", `/api/v1/canvas/${canvasId}/share`, input);
  }

  async deleteShare(canvasId: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/api/v1/canvas/${canvasId}/share`, {
      method: "DELETE",
      headers: this.getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`deleteShare failed: ${res.status}`);
  }

  async getShare(slug: string): Promise<Share> {
    return this.request<Share>("GET", `/api/v1/canvas-share/${slug}`);
  }

  async verifySharePassword(slug: string, password: string): Promise<{ share: Share; shareToken: string }> {
    const res = await fetch(`${this.baseUrl}/api/v1/canvas-share/${slug}/verify`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...this.getAuthHeaders(),
      },
      body: JSON.stringify({ password }),
    });

    const json = await res.json();

    if (!json.success) {
      const msg =
        typeof json.error === "object" && json.error?.message
          ? json.error.message
          : typeof json.error === "string"
            ? json.error
            : `Request failed: ${res.status}`;
      throw new Error(msg);
    }

    return { share: json.data as Share, shareToken: json.shareToken as string };
  }

  // Snapshots
  async listSnapshots(canvasId: string): Promise<Snapshot[]> {
    return this.request<Snapshot[]>("GET", `/api/v1/canvas/${canvasId}/snapshots`);
  }

  async restoreSnapshot(canvasId: string, snapshotId: string): Promise<{ snapshotId: string; canvasId: string; restored: boolean }> {
    return this.request("POST", `/api/v1/canvas/${canvasId}/snapshots/${snapshotId}/restore`);
  }
}
